/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { receiptService } from '../../../services/ReceiptService';
import PremiumFeatureGate from '../PremiumFeatureGate';
import {
  DollarSign,
  Receipt,
  TrendingUp,
  Landmark,
  Download,
  ChevronDown,
  ChevronUp,
  Search,
} from 'lucide-react';

/* ─── helpers ─────────────────────────────────────── */
const fmt = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(n);

const fmtDate = (val) => {
  if (!val) return '—';
  // Firestore Timestamp
  const d = val?.seconds ? new Date(val.seconds * 1000) : new Date(val);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const getYear = (val) => {
  if (!val) return null;
  const d = val?.seconds ? new Date(val.seconds * 1000) : new Date(val);
  return d.getFullYear();
};

/** Normalise receipts - handles both amount and totalAmount fields */
const normalise = (r) => {
  const total = r.totalAmount ?? r.amount ?? 0;
  const fee   = r.platformFee ?? Math.round(total * 0.15);
  const net   = r.eoAmount   ?? total - fee;
  return { ...r, _total: total, _fee: fee, _net: net };
};

/* ─── sub-components ───────────────────────────────── */
const StatCard = ({ icon: Icon, label, value, sub, accent }) => (
  <div className="bg-white rounded-xl border border-grey-200 p-5 flex items-start gap-4">
    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${accent}`}>
      <Icon className="w-5 h-5" />
    </div>
    <div>
      <p className="text-xs text-grey-500 mb-0.5">{label}</p>
      <p className="text-2xl font-semibold text-grey-900">{value}</p>
      {sub && <p className="text-xs text-grey-400 mt-0.5">{sub}</p>}
    </div>
  </div>
);

/* ─── CSV export ───────────────────────────────────── */
const downloadCSV = (rows, year) => {
  const header = ['Date', 'Vendor', 'Event', 'Gross Revenue', 'Platform Fee (15%)', 'Net Payout'];
  const lines = rows.map((r) => [
    fmtDate(r.createdAt),
    `"${r.mpsBusinessName || r.businessName || '—'}"`,
    `"${r.eventName || '—'}"`,
    r._total.toFixed(2),
    r._fee.toFixed(2),
    r._net.toFixed(2),
  ]);
  const csv = [header, ...lines].map((l) => l.join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `pebble-revenue-${year}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

/* ─── main component ───────────────────────────────── */
const EOReportsContent = () => {
  const { user } = useAuth();

  const [allReceipts, setAllReceipts]     = useState([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);
  const [selectedYear, setSelectedYear]   = useState(new Date().getFullYear());
  const [search, setSearch]               = useState('');
  const [sortField, setSortField]         = useState('createdAt');
  const [sortDir, setSortDir]             = useState('desc');
  const [expandedEvents, setExpandedEvents] = useState({});

  /* fetch receipts */
  useEffect(() => {
    if (!user?.uid) return;
    const fetch = async () => {
      setLoading(true);
      try {
        const res = await receiptService.getEOReceipts(user.uid);
        if (res.success) {
          setAllReceipts(res.receipts.map(normalise));
        } else {
          setError('Could not load receipts.');
        }
      } catch (e) {
        setError('Could not load receipts.');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [user?.uid]);

  /* year options derived from data */
  const availableYears = useMemo(() => {
    const years = new Set(allReceipts.map((r) => getYear(r.createdAt)).filter(Boolean));
    years.add(new Date().getFullYear());
    return [...years].sort((a, b) => b - a);
  }, [allReceipts]);

  /* filter by year */
  const yearReceipts = useMemo(
    () => allReceipts.filter((r) => getYear(r.createdAt) === selectedYear),
    [allReceipts, selectedYear],
  );

  /* summary stats */
  const stats = useMemo(() => {
    const totalRevenue = yearReceipts.reduce((s, r) => s + r._total, 0);
    const totalFees    = yearReceipts.reduce((s, r) => s + r._fee,   0);
    const netPayout    = yearReceipts.reduce((s, r) => s + r._net,   0);
    return { totalRevenue, totalFees, netPayout, count: yearReceipts.length };
  }, [yearReceipts]);

  /* per-event breakdown */
  const eventBreakdown = useMemo(() => {
    const map = {};
    yearReceipts.forEach((r) => {
      const key = r.eventId || r.eventName || 'Unknown';
      if (!map[key]) map[key] = { eventName: r.eventName || 'Unknown Event', rows: [], total: 0, fee: 0, net: 0 };
      map[key].rows.push(r);
      map[key].total += r._total;
      map[key].fee   += r._fee;
      map[key].net   += r._net;
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [yearReceipts]);

  /* filtered + sorted transaction list */
  const filteredRows = useMemo(() => {
    let rows = [...yearReceipts];
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter(
        (r) =>
          (r.mpsBusinessName || r.businessName || '').toLowerCase().includes(q) ||
          (r.eventName || '').toLowerCase().includes(q),
      );
    }
    rows.sort((a, b) => {
      let va = a[sortField], vb = b[sortField];
      if (sortField === 'createdAt') {
        va = va?.seconds ?? 0;
        vb = vb?.seconds ?? 0;
      }
      if (sortField === '_total' || sortField === '_net') {
        va = Number(va); vb = Number(vb);
      }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return rows;
  }, [yearReceipts, search, sortField, sortDir]);

  const toggleSort = (field) => {
    if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('desc'); }
  };

  const SortIcon = ({ field }) =>
    sortField === field
      ? sortDir === 'asc'
        ? <ChevronUp className="w-3 h-3 inline ml-1" />
        : <ChevronDown className="w-3 h-3 inline ml-1" />
      : null;

  /* ── render ── */
  return (
    <PremiumFeatureGate featureName="Reports">
      <div className="w-full p-4 sm:p-6 space-y-6">

        {/* Header row */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-grey-900">Reports &amp; Accounting</h1>
            <p className="text-sm text-grey-500 mt-0.5">Revenue breakdown, transaction history, and tax export</p>
          </div>
          <div className="flex items-center gap-2">
            {/* Year selector */}
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className="border border-grey-300 rounded-lg px-3 py-2 text-sm text-grey-700 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300"
            >
              {availableYears.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            {/* Export button */}
            <button
              onClick={() => downloadCSV(filteredRows, selectedYear)}
              disabled={filteredRows.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-grey-900 text-white rounded-lg text-sm font-medium hover:bg-grey-800 transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-500 rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">{error}</div>
        ) : (
          <>
            {/* Summary cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                icon={DollarSign}
                label="Gross Revenue"
                value={fmt(stats.totalRevenue)}
                sub={`${selectedYear}`}
                accent="bg-green-50 text-green-600"
              />
              <StatCard
                icon={Receipt}
                label="Transactions"
                value={stats.count}
                sub="vendor registrations"
                accent="bg-blue-50 text-blue-600"
              />
              <StatCard
                icon={TrendingUp}
                label="Net Payout"
                value={fmt(stats.netPayout)}
                sub="after platform fees"
                accent="bg-purple-50 text-purple-600"
              />
              <StatCard
                icon={Landmark}
                label="Platform Fees"
                value={fmt(stats.totalFees)}
                sub="15% per transaction"
                accent="bg-orange-50 text-orange-600"
              />
            </div>

            {/* Tax note */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-start gap-2 text-sm text-amber-800">
              <span className="text-base leading-none mt-0.5">⚠️</span>
              <span>
                <strong>For tax purposes:</strong> Your taxable income is the <strong>Net Payout</strong> column.
                Platform fees are a deductible business expense. Export CSV for your accountant or to import into QuickBooks / Wave.
              </span>
            </div>

            {/* Per-event breakdown */}
            {eventBreakdown.length > 0 && (
              <div>
                <h2 className="text-sm font-semibold text-grey-700 uppercase tracking-wider mb-3">Per-Event Breakdown</h2>
                <div className="space-y-2">
                  {eventBreakdown.map((evt) => {
                    const isOpen = expandedEvents[evt.eventName];
                    return (
                      <div key={evt.eventName} className="border border-grey-200 rounded-xl overflow-hidden">
                        {/* Event header row */}
                        <button
                          onClick={() => setExpandedEvents((prev) => ({ ...prev, [evt.eventName]: !prev[evt.eventName] }))}
                          className="w-full flex items-center justify-between px-4 py-3 bg-grey-50 hover:bg-grey-100 transition text-left"
                        >
                          <div className="flex items-center gap-3">
                            <span className="font-medium text-grey-900 text-sm">{evt.eventName}</span>
                            <span className="text-xs text-grey-500">{evt.rows.length} vendor{evt.rows.length !== 1 ? 's' : ''}</span>
                          </div>
                          <div className="flex items-center gap-6">
                            <div className="text-right hidden sm:block">
                              <p className="text-xs text-grey-500">Gross</p>
                              <p className="text-sm font-medium text-grey-900">{fmt(evt.total)}</p>
                            </div>
                            <div className="text-right hidden sm:block">
                              <p className="text-xs text-grey-500">Net</p>
                              <p className="text-sm font-medium text-green-700">{fmt(evt.net)}</p>
                            </div>
                            {isOpen ? <ChevronUp className="w-4 h-4 text-grey-400" /> : <ChevronDown className="w-4 h-4 text-grey-400" />}
                          </div>
                        </button>

                        {/* Expanded vendor rows */}
                        {isOpen && (
                          <div className="divide-y divide-grey-100">
                            {evt.rows.map((r, i) => (
                              <div key={r.id || i} className="px-4 py-2.5 flex items-center justify-between text-sm bg-white">
                                <div>
                                  <p className="font-medium text-grey-900">{r.mpsBusinessName || r.businessName || '—'}</p>
                                  <p className="text-xs text-grey-400">{fmtDate(r.createdAt)}</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-grey-700">{fmt(r._total)}</p>
                                  <p className="text-xs text-grey-400">net {fmt(r._net)}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* All transactions */}
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                <h2 className="text-sm font-semibold text-grey-700 uppercase tracking-wider">All Transactions</h2>
                <div className="relative">
                  <Search className="absolute left-2.5 top-2 w-4 h-4 text-grey-400" />
                  <input
                    type="text"
                    placeholder="Search vendor or event…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-8 pr-3 py-1.5 border border-grey-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 w-60"
                  />
                </div>
              </div>

              {filteredRows.length === 0 ? (
                <div className="text-center py-16 text-grey-400">
                  <Receipt className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">No transactions for {selectedYear}</p>
                </div>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-grey-200">
                  <table className="w-full text-sm">
                    <thead className="bg-grey-50 border-b border-grey-200">
                      <tr>
                        <th
                          onClick={() => toggleSort('createdAt')}
                          className="px-4 py-3 text-left text-xs font-medium text-grey-500 uppercase tracking-wider cursor-pointer hover:text-grey-700 select-none"
                        >
                          Date <SortIcon field="createdAt" />
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-grey-500 uppercase tracking-wider">
                          Vendor
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-grey-500 uppercase tracking-wider hidden md:table-cell">
                          Event
                        </th>
                        <th
                          onClick={() => toggleSort('_total')}
                          className="px-4 py-3 text-right text-xs font-medium text-grey-500 uppercase tracking-wider cursor-pointer hover:text-grey-700 select-none"
                        >
                          Gross <SortIcon field="_total" />
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-grey-500 uppercase tracking-wider hidden sm:table-cell">
                          Fee
                        </th>
                        <th
                          onClick={() => toggleSort('_net')}
                          className="px-4 py-3 text-right text-xs font-medium text-grey-500 uppercase tracking-wider cursor-pointer hover:text-grey-700 select-none"
                        >
                          Net <SortIcon field="_net" />
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-grey-100 bg-white">
                      {filteredRows.map((r, i) => (
                        <tr key={r.id || i} className="hover:bg-grey-50 transition">
                          <td className="px-4 py-3 text-grey-600 whitespace-nowrap">{fmtDate(r.createdAt)}</td>
                          <td className="px-4 py-3 font-medium text-grey-900">{r.mpsBusinessName || r.businessName || '—'}</td>
                          <td className="px-4 py-3 text-grey-600 hidden md:table-cell">{r.eventName || '—'}</td>
                          <td className="px-4 py-3 text-right text-grey-900 font-medium whitespace-nowrap">{fmt(r._total)}</td>
                          <td className="px-4 py-3 text-right text-grey-400 hidden sm:table-cell whitespace-nowrap">−{fmt(r._fee)}</td>
                          <td className="px-4 py-3 text-right text-green-700 font-medium whitespace-nowrap">{fmt(r._net)}</td>
                        </tr>
                      ))}
                    </tbody>
                    {/* Totals footer */}
                    <tfoot className="bg-grey-50 border-t-2 border-grey-300">
                      <tr>
                        <td colSpan={3} className="px-4 py-3 text-xs font-semibold text-grey-600 uppercase tracking-wider hidden md:table-cell">
                          Totals ({filteredRows.length} transactions)
                        </td>
                        <td colSpan={3} className="px-4 py-3 text-xs font-semibold text-grey-600 uppercase tracking-wider md:hidden">
                          Totals
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-grey-900 whitespace-nowrap">
                          {fmt(filteredRows.reduce((s, r) => s + r._total, 0))}
                        </td>
                        <td className="px-4 py-3 text-right text-grey-400 hidden sm:table-cell whitespace-nowrap">
                          −{fmt(filteredRows.reduce((s, r) => s + r._fee, 0))}
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-green-700 whitespace-nowrap">
                          {fmt(filteredRows.reduce((s, r) => s + r._net, 0))}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </PremiumFeatureGate>
  );
};

export default EOReportsContent;
